import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { HOST_API } from '../../config-global';
import monitoringService from '../../utils/monitoring/monitoring-service';

// Styles
const styles = {
  container: {
    position: 'fixed',
    bottom: '10px',
    right: '10px',
    zIndex: 9999,
    fontFamily: 'Arial, sans-serif',
    fontSize: '12px',
    padding: '5px 10px',
    borderRadius: '3px',
    cursor: 'pointer',
    userSelect: 'none',
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
    opacity: process.env.NODE_ENV === 'production' ? 0 : 0.7,
  },
  healthyIndicator: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: '#4CAF50', // Green
  },
  unhealthyIndicator: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: '#F44336', // Red
  },
  warningIndicator: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: '#FFC107', // Yellow/Amber
  },
  modal: {
    position: 'fixed',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    backgroundColor: '#fff',
    padding: '20px',
    borderRadius: '5px',
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.2)',
    zIndex: 10000,
    maxWidth: '500px',
    width: '90%',
  },
  backdrop: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 9999,
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '15px',
    borderBottom: '1px solid #eee',
    paddingBottom: '10px',
  },
  closeButton: {
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    fontSize: '16px',
  },
  title: {
    margin: 0,
    fontSize: '18px',
  },
  serviceRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '8px 0',
    borderBottom: '1px solid #eee',
  },
  serviceStatus: {
    fontWeight: 'bold',
  },
  healthy: {
    color: '#4CAF50',
  },
  unhealthy: {
    color: '#F44336',
  },
  warning: {
    color: '#FFC107',
  },
  metricsSection: {
    marginTop: '15px',
  },
  metricsTitle: {
    fontSize: '16px',
    margin: '10px 0',
  },
  metricRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '5px 0',
  },
};

// Add bypass mode indicator
const BypassIndicator = () => {
  const isAuthBypass = process.env.NODE_ENV !== 'production';

  if (!isAuthBypass) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: '10px',
        right: '10px',
        background: '#FFF3CD',
        color: '#856404',
        padding: '4px 8px',
        borderRadius: '4px',
        fontSize: '12px',
        zIndex: 9999,
        border: '1px solid #FFEEBA',
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        cursor: 'help',
      }}
      title="Authentication bypass is enabled in this environment"
    >
      <span style={{ fontSize: '16px' }}>🔓</span>
      Demo Mode
    </div>
  );
};

const HealthCheck = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [healthStatus, setHealthStatus] = useState({
    status: 'unknown',
    api: {
      status: 'unknown',
      lastChecked: null,
    },
    frontend: {
      status: 'healthy', // Frontend is running if this component is rendered
      performanceScore: 'N/A',
      memoryUsage: 'N/A',
    },
    metrics: {
      apiLatency: 'N/A',
      errorRate: 0,
      failedApiCalls: 0,
      successfulApiCalls: 0,
    },
  });

  // Check API health status
  const checkApiHealth = async () => {
    try {
      const startTime = Date.now();
      const response = await axios.get(`${HOST_API}/api/health`, { timeout: 5000 });
      const latency = Date.now() - startTime;

      setHealthStatus(prev => ({
        ...prev,
        status: response.data.status === 'healthy' ? 'healthy' : 'unhealthy',
        api: {
          status: response.data.status,
          lastChecked: new Date().toLocaleTimeString(),
          details: response.data,
        },
        metrics: {
          ...prev.metrics,
          apiLatency: `${latency}ms`,
          successfulApiCalls: prev.metrics.successfulApiCalls + 1,
        },
      }));
    } catch (error) {
      console.error('Health check failed:', error);

      setHealthStatus(prev => ({
        ...prev,
        status: 'unhealthy',
        api: {
          status: 'unhealthy',
          lastChecked: new Date().toLocaleTimeString(),
          error: error.message,
        },
        metrics: {
          ...prev.metrics,
          failedApiCalls: prev.metrics.failedApiCalls + 1,
          errorRate: Math.round(
            (prev.metrics.failedApiCalls + 1) /
            (prev.metrics.failedApiCalls + prev.metrics.successfulApiCalls + 1) * 100,
          ),
        },
      }));
    }
  };

  // Check frontend health (memory, performance, etc.)
  const checkFrontendHealth = () => {
    // Memory usage
    let memoryUsage = 'N/A';
    if (window.performance && window.performance.memory) {
      const usedHeapSize = Math.round(window.performance.memory.usedJSHeapSize / (1024 * 1024));
      const totalHeapSize = Math.round(window.performance.memory.totalJSHeapSize / (1024 * 1024));
      memoryUsage = `${usedHeapSize}MB / ${totalHeapSize}MB`;
    }

    // Calculate rough performance score based on load time
    let performanceScore = 'N/A';
    if (window.performance && window.performance.timing) {
      const loadTime = window.performance.timing.loadEventEnd - window.performance.timing.navigationStart;
      // A simple scoring algorithm (lower is better, under 1000ms is good)
      if (loadTime < 1000) {
        performanceScore = 'Excellent (A)';
      } else if (loadTime < 2000) {
        performanceScore = 'Good (B)';
      } else if (loadTime < 3000) {
        performanceScore = 'Average (C)';
      } else if (loadTime < 5000) {
        performanceScore = 'Poor (D)';
      } else {
        performanceScore = 'Very Poor (F)';
      }
    }

    setHealthStatus(prev => ({
      ...prev,
      frontend: {
        ...prev.frontend,
        status: 'healthy',
        performanceScore,
        memoryUsage,
      },
    }));
  };

  useEffect(() => {
    // Initial health check
    checkApiHealth();
    checkFrontendHealth();

    // Setup interval for periodic checks
    const interval = setInterval(() => {
      checkApiHealth();
      checkFrontendHealth();
    }, 60000); // Check every minute

    // Track component mount as an application event
    monitoringService.trackEvent('healthCheckComponentMounted');

    return () => {
      clearInterval(interval);
    };
  }, []);

  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
    if (!isModalOpen) {
      // Refresh health check when opening modal
      checkApiHealth();
      checkFrontendHealth();
      monitoringService.trackEvent('healthCheckModalOpened');
    }
  };

  // Determine overall status indicator color
  const getStatusIndicator = () => {
    switch (healthStatus.status) {
    case 'healthy':
      return styles.healthyIndicator;
    case 'unhealthy':
      return styles.unhealthyIndicator;
    default:
      return styles.warningIndicator;
    }
  };

  return (
    <>
      <BypassIndicator />
      {/* Health indicator dot in corner */}
      <div style={styles.container} onClick={toggleModal}>
        <div style={getStatusIndicator()} />
        <span>{process.env.NODE_ENV === 'production' ? '' : 'System Status'}</span>
      </div>

      {/* Health check modal */}
      {isModalOpen && (
        <>
          <div style={styles.backdrop} onClick={toggleModal} />
          <div style={styles.modal}>
            <div style={styles.modalHeader}>
              <h3 style={styles.title}>System Health Status</h3>
              <button style={styles.closeButton} onClick={toggleModal}>×</button>
            </div>

            <div>
              <div style={styles.serviceRow}>
                <span>API Service</span>
                <span
                  style={{
                    ...styles.serviceStatus,
                    ...(healthStatus.api.status === 'healthy' ? styles.healthy : styles.unhealthy),
                  }}
                >
                  {healthStatus.api.status === 'healthy' ? 'Healthy' : 'Unhealthy'}
                </span>
              </div>

              <div style={styles.serviceRow}>
                <span>Frontend Application</span>
                <span
                  style={{
                    ...styles.serviceStatus,
                    ...(healthStatus.frontend.status === 'healthy' ? styles.healthy : styles.unhealthy),
                  }}
                >
                  {healthStatus.frontend.status === 'healthy' ? 'Healthy' : 'Unhealthy'}
                </span>
              </div>

              <div style={styles.serviceRow}>
                <span>Last Checked</span>
                <span>{healthStatus.api.lastChecked || 'N/A'}</span>
              </div>

              <div style={styles.metricsSection}>
                <h4 style={styles.metricsTitle}>Performance Metrics</h4>

                <div style={styles.metricRow}>
                  <span>API Latency</span>
                  <span>{healthStatus.metrics.apiLatency}</span>
                </div>

                <div style={styles.metricRow}>
                  <span>Frontend Performance</span>
                  <span>{healthStatus.frontend.performanceScore}</span>
                </div>

                <div style={styles.metricRow}>
                  <span>Memory Usage</span>
                  <span>{healthStatus.frontend.memoryUsage}</span>
                </div>

                <div style={styles.metricRow}>
                  <span>API Error Rate</span>
                  <span
                    style={{
                      ...(healthStatus.metrics.errorRate > 5 ? styles.unhealthy :
                        healthStatus.metrics.errorRate > 0 ? styles.warning : styles.healthy),
                    }}
                  >
                    {healthStatus.metrics.errorRate}%
                  </span>
                </div>
              </div>
            </div>

            <div style={{ marginTop: '20px', fontSize: '12px', color: '#666' }}>
              Environment: {process.env.NODE_ENV || 'development'}<br />
              Version: {process.env.REACT_APP_VERSION || '0.0.0'}
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default HealthCheck;
